import { Injectable } from '@angular/core';
import { DateTime } from '@app/@core/models';
import { APP_NAME_TAG } from '@app/settings.config';
import moment from 'moment';
import { LocalStorageService } from '../../@core/services/local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  private readonly PREFIX = APP_NAME_TAG;
  private readonly AUTH_KEY_PREFIX = 'auth';
  private readonly ACCESS_TOKEN_KEY = 'accessToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken'
  private readonly ACCESS_TOKEN_EXPIRATION_DATE = 'accessTokenExpirationDate';
  private readonly REFRESH_TOKEN_EXPIRATION_DATE = 'refreshTokenExpirationDate';
  private readonly USER_ID_KEY = 'user';

  constructor(private localStorage: LocalStorageService) {}

  save(accessToken: string, refreshToken: string, accessTokenExpirationDate: DateTime, refreshTokenExpirationDate: DateTime, userId: string): void {
    this.set(this.ACCESS_TOKEN_KEY, accessToken);
    this.set(this.REFRESH_TOKEN_KEY, refreshToken);
    this.set(this.ACCESS_TOKEN_EXPIRATION_DATE, accessTokenExpirationDate);
    this.set(this.REFRESH_TOKEN_EXPIRATION_DATE, refreshTokenExpirationDate);
    this.set(this.USER_ID_KEY, userId);
  }

  accessToken(): string {
    return this.get(this.ACCESS_TOKEN_KEY) as string;
  }

  isAccessTokenExpired(): boolean {
    return moment(this.accessTokenExpirationDate()) <= moment();
  }

  refreshToken(): string {
    return this.get(this.REFRESH_TOKEN_KEY) as string;
  }

  isRefreshTokenExpired(): boolean {
    return moment(this.refreshTokenExpirationDate()) <= moment();
  }

  accessTokenExpirationDate(): DateTime {
    return this.get(this.ACCESS_TOKEN_EXPIRATION_DATE) as DateTime;
  }

  refreshTokenExpirationDate(): DateTime {
    return this.get(this.REFRESH_TOKEN_EXPIRATION_DATE) as DateTime;
  }

  userId(): string {
    return this.get(this.USER_ID_KEY) as string;
  }

  has(): boolean {
    return !!this.accessToken() && !!this.refreshToken() && !!this.userId();
  }

  reset() {
    this.remove(this.ACCESS_TOKEN_KEY);
    this.remove(this.REFRESH_TOKEN_KEY);
    this.remove(this.ACCESS_TOKEN_EXPIRATION_DATE);
    this.remove(this.REFRESH_TOKEN_EXPIRATION_DATE);
    this.remove(this.USER_ID_KEY);
  }

  private get(key: string) {
    return this.localStorage.get(this.getKey(key));
  }

  private set(key: string, value: string) {
    this.localStorage.set(this.getKey(key), value);
  }

  private remove(key: string) {
    this.localStorage.remove(this.getKey(key));
  }

  private getKey(key: string) {
    return `${this.AUTH_KEY_PREFIX}_${key}`;
  }
}
